import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  Send, 
  Lock, 
  User, 
  AlertCircle,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isSubscriber } = useAuth();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const canSendMessage = isAuthenticated && (isSubscriber || user?.is_admin);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!canSendMessage) {
      toast.error('Please subscribe to send messages');
      return;
    }

    if (!subject.trim() || !message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/support/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          subject: subject.trim(),
          message: message.trim()
        })
      });

      if (response.ok) {
        setSent(true);
        toast.success('Message sent successfully! We\'ll respond within 24-48 hours.');
        setSubject('');
        setMessage('');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to send message');
      }
    } catch (err) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="font-display text-3xl font-bold uppercase tracking-tight mb-2">
              Contact Us
            </h1>
            <p className="text-muted-foreground">
              Have questions or feedback? We'd love to hear from you.
            </p>
          </div>

          {!isAuthenticated ? (
            <Card className="border-border">
              <CardHeader className="text-center">
                <div className="w-12 h-12 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="w-6 h-6 text-yellow-400" />
                </div>
                <CardTitle className="font-display uppercase">Sign In Required</CardTitle>
                <CardDescription>
                  Please sign in or create an account to contact support.
                </CardDescription>
              </CardHeader>
              <CardFooter className="flex justify-center gap-4">
                <Button onClick={() => navigate('/login')} variant="outline">
                  Sign In
                </Button>
                <Button onClick={() => navigate('/register')} className="bg-red-500 hover:bg-red-600">
                  Create Account
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardFooter>
            </Card>
          ) : !canSendMessage ? (
            <Card className="border-border">
              <CardHeader className="text-center">
                <div className="w-12 h-12 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-6 h-6 text-yellow-400" />
                </div>
                <CardTitle className="font-display uppercase">Subscriber Feature</CardTitle>
                <CardDescription>
                  Direct messaging to support is available for subscribers only.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-sm text-muted-foreground mb-4">
                  Subscribe to get direct access to our support team and premium features.
                </p>
                <Badge variant="outline" className="text-yellow-400 border-yellow-400/30">
                  Logged in as: {user?.email}
                </Badge>
              </CardContent>
              <CardFooter className="flex justify-center">
                <Button onClick={() => navigate('/pricing')} className="bg-red-500 hover:bg-red-600">
                  View Subscription Plans
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardFooter>
            </Card>
          ) : sent ? (
            <Card className="border-border">
              <CardHeader className="text-center">
                <div className="w-12 h-12 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-6 h-6 text-green-400" />
                </div>
                <CardTitle className="font-display uppercase text-green-400">Message Sent!</CardTitle>
                <CardDescription>
                  Thank you for reaching out. We'll respond within 24-48 hours.
                </CardDescription>
              </CardHeader>
              <CardFooter className="flex justify-center">
                <Button onClick={() => setSent(false)} variant="outline">
                  Send Another Message
                </Button>
              </CardFooter>
            </Card>
          ) : (
            <Card className="border-border">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Badge className="bg-green-500/10 text-green-400 border-green-500/20">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Subscriber
                  </Badge>
                </div>
                <CardTitle className="font-display uppercase">Send a Message</CardTitle>
                <CardDescription>
                  Our team typically responds within 24-48 hours.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Subject</label>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="What's this about?"
                      className="w-full px-4 py-2 bg-secondary border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-red-500/50"
                      data-testid="contact-subject"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Message</label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="How can we help you?"
                      rows={6}
                      className="w-full px-4 py-2 bg-secondary border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-red-500/50 resize-none"
                      data-testid="contact-message"
                      required
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-red-500 hover:bg-red-600"
                    disabled={sending}
                    data-testid="contact-submit"
                  >
                    {sending ? (
                      <>Sending...</>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* FAQ or additional info */}
          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground">
              <AlertCircle className="w-4 h-4 inline mr-1" />
              For urgent matters, messages are prioritized for subscribers.
            </p>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
